import { createI18n } from 'vue-i18n'
import type {
  DateTimeFormatKey,
  I18nKey,
  NumberFormatKey,
} from '@/locales/i18n.types'
import {
  datetimeFormats,
  numberFormats,
} from '@team-milva/lab-constants/i18nFormats'
import { ENV, HOST } from '@/globals/typescript/utils/host'
import { merge } from 'lodash-es'
import {
  LAB_CUSTOMER_LOCALES,
  LAB_MANAGEMENT_LOCALES,
  type TLabManagementLocale,
} from '@team-milva/lab-models/language'

// A report renders in the *order's* locale, which is a TLabCustomerLocale — da,
// en, de, no or fr — while the UI itself only ever runs in da or en. Without a
// message bundle for the other three, the report preview silently falls back to
// English and stops matching the PDF lab-cloud actually sends (MIL-268).
//
// Only the `general` area is loaded for those extra locales: that is where the
// report chrome lives, and it is the only area with all five locale files. The
// `lab-management` area is UI copy and correctly only has da/en — fetching
// lab-management/no.json would 404 and reject the whole locale.
export const REPORT_ONLY_LOCALES = LAB_CUSTOMER_LOCALES.filter(
  (locale) => !LAB_MANAGEMENT_LOCALES.includes(locale as TLabManagementLocale)
)

// Resolved lazily — HOST.translations is only populated once initEnvironment()
// has run during bootstrap. On localhost the Vite proxy handles /i18n.
function getI18nHost(): string {
  return (
    (import.meta.env.VITE_I18N_HOST as string | undefined) ??
    (ENV.isLocalhost ? '' : HOST.translations)
  )
}

// Only warn once per key for the "missing everywhere" case
const warnedMissingEverywhere = new Set<string>()

export const i18n = createI18n({
  legacy: false as const,
  globalInjection: true as const,
  locale: 'en' as TLabManagementLocale,
  fallbackLocale: 'en' as TLabManagementLocale,

  missingWarn: false,
  fallbackWarn: false,

  numberFormats,
  datetimeFormats,

  missing: (locale, key) => {
    if (key.startsWith('W: ')) return key.replace(/^W:\s*/, '')

    if (ENV.isDev) {
      const existsSomewhere = LAB_MANAGEMENT_LOCALES.some((l) =>
        i18n.global.te(key, l)
      )

      if (
        key.includes(' ') &&
        !existsSomewhere &&
        !warnedMissingEverywhere.has(key)
      ) {
        warnedMissingEverywhere.add(key)
        console.warn(`!! Remember to prefix temporary keys with "W: " (${key})`)
        return key
      }

      console.warn(`Not found '${key}' key in '${locale}' locale messages.`)
    }

    return key
  },
})

export const t = (key: I18nKey, params?: Record<string, unknown>) =>
  params === undefined ? i18n.global.t(key) : i18n.global.t(key, params)

// Locale-explicit variant, mirroring `n` and `d` below. Needed wherever the
// string being produced is not for the current user — a generated report
// renders in the order's report locale, which is frequently a different one.
// `locale` is widened to string because it comes from data (an order's report
// locale), not from the UI's own locale union — cast where vue-i18n needs it.
export const tIn = (
  locale: string,
  key: I18nKey,
  params?: Record<string, unknown>
) =>
  i18n.global.t(key, params ?? {}, {
    locale: locale as TLabManagementLocale,
  })

export const n = (
  value: number,
  format?: NumberFormatKey | Intl.NumberFormatOptions,
  locale?: TLabManagementLocale
) =>
  locale !== undefined
    ? i18n.global.n(value, format as string, locale)
    : format !== undefined
      ? i18n.global.n(value, format as string)
      : i18n.global.n(value)

/**
 * Maps an app *content* locale onto the locale used for `Intl` *display*
 * formatting.
 *
 * The content locale ('en' / 'da') is not only a message-bundle key — it is
 * also an `ITextObj` key and a Firestore write key (`name.${locale}`), so it
 * can never carry a region subtag without forking the stored data.
 *
 * But bare 'en' resolves to en-US in ICU, which formats dates month-first
 * (`Feb 12, 2025`) — inconsistent with Danish (`12. feb. 2025`) and with the
 * direct `Intl.DateTimeFormat(..., 'en-GB', ...)` call sites already used for
 * order creation, DHL cards and the dashboard graph axis. This map is the
 * display-only counterpart, keeping the two concerns separate.
 *
 * Verified: 'da'/'da-DK' and 'de'/'de-DE' produce byte-identical output for
 * every format in `datetimeFormats` — they are listed for explicitness, and
 * only 'en' actually changes anything.
 *
 * NOTE: duplicated in lab-admin's `src/locales/index.ts`. It belongs in
 * `@team-milva/lab-constants/i18nFormats` alongside `datetimeFormats`, but a
 * new export there cannot be consumed until that package is released — move it
 * on the next `lab-constants` release and delete both copies.
 */
const INTL_DISPLAY_LOCALES: Record<string, string> = {
  en: 'en-GB',
  da: 'da-DK',
  de: 'de-DE',
}

/** Falls through unchanged for any locale with no explicit display mapping. */
const resolveIntlLocale = (locale: string): string =>
  INTL_DISPLAY_LOCALES[locale] ?? locale

export const d = (
  value: Date | number,
  format: DateTimeFormatKey,
  localeOrOptions?: TLabManagementLocale | Intl.DateTimeFormatOptions,
  maybeOptions?: Intl.DateTimeFormatOptions
) => {
  // The *content* locale — an ITextObj/Firestore key, never region-tagged.
  const contentLocale =
    typeof localeOrOptions === 'string'
      ? localeOrOptions
      : (i18n.global.locale.value as TLabManagementLocale)

  const extraOptions =
    typeof localeOrOptions === 'object' ? localeOrOptions : maybeOptions

  // getDateTimeFormat() is keyed by *locale*, then by format name. Passing the
  // format name straight in returns {} and silently drops the whole format,
  // so resolve it in two steps.
  const formatOptions = (
    i18n.global.getDateTimeFormat(contentLocale) as unknown as
      | Record<string, Intl.DateTimeFormatOptions>
      | undefined
  )?.[format]

  // Resolved options + an explicit display locale, so Intl gets 'en-GB'
  // rather than bare 'en' (which ICU resolves to en-US). See resolveIntlLocale.
  return i18n.global.d(
    value,
    { ...formatOptions, ...extraOptions },
    // vue-i18n types this param as a *registered* locale, but at runtime it
    // passes straight to Intl and accepts any BCP-47 tag once explicit
    // options are supplied (verified against 'en-GB'). The cast asserts
    // that, rather than registering a display-only locale bundle.
    resolveIntlLocale(contentLocale) as TLabManagementLocale
  )
}

/**
 * Fetch latest i18n version and load general messages for all locales in LAB_MANAGEMENT_LOCALES.
 * Uses remote-only messages (no local/test overlays). Falls back silently on error.
 * When @team-milva/i18n-keys is locally linked, loads from the virtual module (raw JSON files)
 * instead of the CDN — no env var needed.
 */
export async function loadRemoteI18n(): Promise<void> {
  try {
    // Deliberately the BUILD mode, not ENV.isDev: the local bundle only exists when
    // @team-milva/i18n-keys is locally linked (package.links.json), which is a
    // property of how this bundle was built, not of the environment it runs in.
    // Gating on ENV.isDev would make stage and dev deploys fetch this whole
    // translations chunk at startup only to find no marker and fall through to the CDN.
    if (import.meta.env.DEV) {
      try {
        const mod = await import('@team-milva/i18n-keys/latest-translations')
        if ((mod as { __isLocalBundle__?: boolean }).__isLocalBundle__) {
          const { latestTranslations } = mod
          const localGeneral = latestTranslations.translations.general ?? {}
          const localLabManagement =
            latestTranslations.translations['lab-management'] ?? {}
          for (const locale of LAB_MANAGEMENT_LOCALES) {
            const merged = merge(
              {},
              (localGeneral[locale] ?? {}) as Record<string, unknown>,
              (localLabManagement[locale] ?? {}) as Record<string, unknown>
            ) as Record<string, unknown>
            i18n.global.setLocaleMessage(locale, merged)
          }
          for (const locale of REPORT_ONLY_LOCALES) {
            i18n.global.setLocaleMessage(
              locale,
              merge({}, (localGeneral[locale] ?? {}) as Record<string, unknown>)
            )
          }
          return
        }
      } catch {
        // local bundle not available — fall through to CDN
      }
    }

    const i18nHost = getI18nHost()

    const latestRes = await fetch(`${i18nHost}/i18n/latest.json`, {
      cache: 'no-store',
    })
    if (!latestRes.ok) throw new Error(`latest.json ${latestRes.status}`)
    const latest = await latestRes.json()
    const version: string =
      typeof latest === 'string'
        ? latest
        : typeof latest?.version === 'string'
          ? latest.version
          : ''
    if (!version) throw new Error('Missing version in latest.json')

    // One parallel wave for every locale — UI locales pull `general` + `lab-management`,
    // report-only locales pull `general` alone. Loading them in two sequential waves
    // would add a whole round trip to app boot, which `main.ts` awaits before mount.
    const targets = [
      ...LAB_MANAGEMENT_LOCALES.map((locale) => ({ locale, withAppArea: true })),
      ...REPORT_ONLY_LOCALES.map((locale) => ({ locale, withAppArea: false })),
    ]

    const localeResults = await Promise.allSettled(
      targets.map(async ({ locale, withAppArea }) => {
        const generalRes = await fetch(
          `${i18nHost}/i18n/${version}/general/${locale}.json`
        )
        if (!generalRes.ok) throw new Error('Failed locale fetch')
        const general = (await generalRes.json()) as Record<string, unknown>

        if (!withAppArea) return { locale, merged: general }

        const labManagementRes = await fetch(
          `${i18nHost}/i18n/${version}/lab-management/${locale}.json`
        )
        if (!labManagementRes.ok) throw new Error('Failed locale fetch')
        const labManagement = (await labManagementRes.json()) as Record<string, unknown>

        return {
          locale,
          merged: merge({}, general, labManagement) as Record<string, unknown>,
        }
      })
    )

    for (const [i, result] of localeResults.entries()) {
      if (result.status === 'fulfilled') {
        i18n.global.setLocaleMessage(result.value.locale, result.value.merged)
      } else if (ENV.isDev) {
        // A report-only locale failing is not fatal — the app stays fully usable,
        // only a non-da/en report preview degrades to its fallback.
        console.warn(
          `[i18n] Failed to load locale '${targets[i]?.locale}':`,
          result.reason
        )
      }
    }
  } catch (err) {
    if (ENV.isDev) {
      console.warn('[i18n] Remote/local load skipped:', err)
    }
    // Fallback: keep existing local messages
  }
}

